import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCampaign from '@hooks/useCampaign'
import useConversationContainer from '@hooks/useConversationContainer'

const MODAL_KEY = 'ManageCampaignTestStatusModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateCampaign: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageCampaignTestStatusModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateCampaign } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign: initCampaign } = modalPayload

  const {
    callbacks: { updateCampaign: updateFn },
    campaign,
    updating,
  } = useCampaign(initCampaign)

  const isTestCampaign = campaign.sandbox

  const conversationContainerPayload = useConversationContainer({
    // @ts-expect-error TS2739
    conversation: {
      agency_id: campaign?.agency_id,
      campaign_id: campaign?.id,
      subject_id: campaign?.id,
      subject_type: 'Campaign',
    },
  })

  const {
    callbacks: { createCommentWithConversation },
    commentForm,
  } = conversationContainerPayload

  const {
    entityState,
    entityState: { body },
    errors,
    handlers,
  } = commentForm

  const updateCampaignPayload = {
    callbacks: {
      action: updateFn,
      afterAction: () => {
        createCommentWithConversation(entityState)
        closeModal()
      },
    },
    entityParams: {
      sandbox: !isTestCampaign,
    },
    toastText: 'Campaign Updated',
  }

  const saveEnabled = !!body
  const saveButtonText = isTestCampaign ? 'Reactivate Campaign' : 'Set Campaign As Test'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Test" titleSecondLine="Campaign" />

      <SidebarModal.Content>
        {!isTestCampaign && (
          <SidebarNoticeBox>
            <SidebarNoticeBox.Title>This will flag the campaign as Test</SidebarNoticeBox.Title>
            <SidebarNoticeBox.Paragraph>
              Once a campaign is set as a Test Campaign, it will be excluded from some reports
              and will not trigger a campaign fee.
              &nbsp;Please add a note to describe the reason for setting the campaign as test.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        )}

        {isTestCampaign && (
          <SidebarNoticeBox>
            <SidebarNoticeBox.Title>This will reactivate the campaign</SidebarNoticeBox.Title>
            <SidebarNoticeBox.Paragraph>
              Please add a note to describe the reason for reactivating the campaign.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        )}

        <Form>
          <Form.Field label="* Note or Description" errorMessage={errors.body}>
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="body"
              style={{ height: 120, resize: 'vertical' }}
              value={body}
              {...handlers}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => updateCampaign(updateCampaignPayload)}
          size="large"
        >
          {saveEnabled ? saveButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageCampaignTestStatusModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
